import React, { FC, useMemo } from 'react';
import {
  useWidgetsSetup,
  WidgetsSetup,
} from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupProvider';
import Skeleton from 'react-loading-skeleton';
import { WidgetVariant } from 'app/modules/posts/components/accordionWidget/constants/widgetVariant';

type WidgetProps = WidgetsSetup & {
  setExpanded: (isExpanded: boolean) => void;
};

type Props = {
  children: Partial<
    Record<WidgetVariant, (widget: WidgetProps) => JSX.Element | null>
  >;
};

const WidgetsSetupLayout: FC<Props> = ({ children }) => {
  const { widgetsSetup, isLoading, defaultWidgetsSetup, setWidgetExpanded } =
    useWidgetsSetup();

  const widgets = useMemo(() => {
    if (isLoading) {
      return Object.keys(children).map((key) => (
        <div key={`loading-${key}`} className="tw-overflow-hidden tw-rounded">
          <Skeleton height={200} />
        </div>
      ));
    }

    const _widgetSetup = widgetsSetup ?? defaultWidgetsSetup;

    const orderedWidgets = _widgetSetup.map((widget, index) => {
      const widgetComponent = children[widget.key];

      if (!widgetComponent) return null;

      const widgetProps: WidgetProps = {
        ...widget,
        setExpanded: (isExpanded: boolean) =>
          setWidgetExpanded(widget.key, isExpanded),
      };

      const renderedComponent = widgetComponent(widgetProps);
      if (renderedComponent === null) return null;

      return React.cloneElement(renderedComponent, {
        key: `${widget.key}_${widget.isExpanded ? 'expanded' : 'collapsed'}`,
      });
    });

    const unorderedWidgets = Object.entries(children)
      .filter(([key]) => !_widgetSetup.some((widget) => widget.key === key))
      .map(([key, render]) => {
        if (typeof render !== 'function') return null;

        const widgetProps: WidgetProps = {
          key: key as WidgetVariant,
          isExpanded: false,
          setExpanded: (isExpanded: boolean) =>
            setWidgetExpanded(key as WidgetVariant, isExpanded),
        };

        const renderedComponent = render(widgetProps);
        if (renderedComponent === null) return null;

        return React.cloneElement(renderedComponent, {
          key: `unordered-${key}_collapsed`,
        });
      });

    return [orderedWidgets, ...unorderedWidgets];
  }, [
    children,
    widgetsSetup,
    defaultWidgetsSetup,
    isLoading,
    setWidgetExpanded,
  ]);

  return <>{widgets}</>;
};

export default WidgetsSetupLayout;
